import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Typography,
  Space,
  Row,
  Col,
  Tag,
  Divider,
  List,
  Popconfirm,
  App,
  Dropdown,
  Spin,
  Drawer,
  Collapse
} from 'antd';
import type { MenuProps } from 'antd';
import {
  ArrowLeftOutlined,
  CaretRightOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  PrinterOutlined,
  CreditCardOutlined,
  PercentageOutlined,
  EnvironmentOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  MoreOutlined,
  TeamOutlined,
  HistoryOutlined,
  SwapOutlined,
  PlusCircleOutlined,
  MessageOutlined,
  ScissorOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { Table } from '../types/tables';
import { Order, OrderItem as OrderItemType } from '../types/orders';
import { useGetActiveOrderByTableQuery, useGetOrderDiscountsQuery, useRemoveDiscountMutation, useSetOrderItemSeatMutation } from '../services/ordersApi';
import SeatPill from '../components/common/SeatPill';
import { seatLabel, maxSeatIn, MAX_SEATS } from '../utils/seats';
import { itemDiscountTag, itemDiscountsById, netItemTotal, roundCents, withDiscountLabels } from '../utils/discounts';
import { useGetRestaurantSettingsQuery } from '../services/restaurantsApi';
import { KitchenOrderDto, KitchenOrderStatus, KdsOrderItemDto } from '../types/kitchen';
import {
  useUpdateKitchenOrderItemsStatusMutation,
  useComandearOrderItemsMutation,
} from '../services/kitchenApi';
import { useHasProductionCentersQuery } from '../services/productionCenterApi';
import { useGetBoolSettingQuery } from '../services/settingsApi';
import { useGetProductsQuery } from '../services/productsApi';
import { printAgentService } from '../services/printAgentService';
import { buildOrderTicketPreview, withPrintAnimation } from '../services/printAnimation';
import { bluetoothPrintService } from '../services/bluetoothPrintService';
import { useCloudPrint } from '../hooks/useCloudPrint';
import { useGetFacturacionConfigQuery } from '../services/facturacionApi';
import { ComandearContent } from '../components/tables/modals/ComandearModal';
import PreCheckModal from '../components/tables/modals/PreCheckModal';
import EditGuestsModal from '../components/tables/modals/EditGuestsModal';
import { formatGuests } from '../utils/guests';
import PaymentModal from '../components/tables/modals/PaymentModal';
import DiscountModal from '../components/tables/modals/DiscountModal';
import ItemDiscountModal from '../components/tables/modals/ItemDiscountModal';
import SplitBillModal from '../components/splits/SplitBillModal';
import OrderHistory from '../components/orders/OrderHistory';
import ResendOrderModal from '../components/orders/modals/ResendOrderModal';
import DailyMenuSelectionModal from '../components/orders/modals/DailyMenuSelectionModal';
import TableHistory from '../components/tables/TableHistory';
import { useResponsive } from '../hooks/useResponsive';
import { useOwnsBottomEdge } from '../hooks/useBottomEdge';
import { useAppSelector } from '../hooks/redux';
import { canProcessPayments, canApplyDiscounts } from '../utils/roleHelpers';
import { isOrderAlreadySplitError, ORDER_ALREADY_SPLIT_NOTICE } from '../utils/paymentErrors';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';
import { useIncrementalDailyMenuSend } from '../hooks/useIncrementalDailyMenuSend';
import { groupSelectionsByCourse } from '../utils/dailyMenuGrouping';
import { ProductOrderItem } from '../types/products';
import { formatOrderNumber } from '../utils/orderNumber';
import { shouldUseCloudPrint } from '../utils/cloudPrintRoute';

const { Title, Text } = Typography;
const { Panel } = Collapse;

// One table action, rendered identically in the desktop card, the mobile
// bottom bar and the mobile drawer.
interface TableAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  buttonType?: 'primary' | 'default';
  style?: React.CSSProperties;
  loading?: boolean;
}

interface TableManagementProps {
  table: Table;
  onBack: () => void;
  onComandear?: (table: Table) => void;
  onPrecuenta?: (table: Table, order: Order) => Promise<void>;
  onLiquidar?: (table: Table, order: Order) => void;
  onDescuentos?: (table: Table, order: Order) => void;
  onCloseTable?: (tableId: string) => void;
  onCancelOrderItem?: (orderItemId: string, orderId?: string) => void;
  onReopenTable?: (tableId: string) => void;
  onTransferTable?: (table: Table) => void;
  onTableUpdate?: () => void;
  onProcessPayment?: (payments: Array<{ id?: string; methodId: string; amount: number; reference?: string }>, tip: number, tableData?: Table, orderData?: Order) => Promise<void>;
  onPrintReceipt?: (payments: Array<{ methodId: string; amount: number; reference?: string }>, tip: number, tableData?: Table, orderData?: Order) => void;
  /** Cobro hecho en terminal MP (el backend ya registró el pago): solo housekeeping. */
  onTerminalPaymentSettled?: (tableData?: Table, orderData?: Order) => Promise<void>;
  onApplyDiscountToOrder?: (discountData: { type: 'percentage' | 'amount'; value: number; reason: string; notes?: string; authorizedBy: string; orderId?: string; }) => Promise<void>;
  /** Montado como panel lateral junto a la cuadrícula de mesas (escritorio). */
  embedded?: boolean;
  /** Avisa al contenedor que se abrió/cerró comandear, para que el panel deje
      pasar a pantalla completa mientras se eligen productos. */
  onComandearVisibleChange?: (visible: boolean) => void;
}

const TableManagement: React.FC<TableManagementProps> = ({
  table,
  onBack,
  onComandear,
  onPrecuenta,
  onLiquidar,
  onDescuentos,
  onCloseTable,
  onCancelOrderItem,
  onReopenTable,
  onTransferTable,
  onTableUpdate,
  onProcessPayment,
  onPrintReceipt,
  onTerminalPaymentSettled,
  onApplyDiscountToOrder,
  embedded = false,
  onComandearVisibleChange,
}) => {
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { user } = useAppSelector((state) => state.auth);
  // Static `import { message } from 'antd'` silently no-ops under AntD v5;
  // message/modal must come from App.useApp().
  const { message, modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [comandearVisible, setComandarVisible] = useState(false);

  // Comandear ocupa la pantalla completa aun montado como panel: elegir productos
  // necesita todo el ancho, y el contenedor tiene que retirar la cuadrícula.
  useEffect(() => {
    onComandearVisibleChange?.(comandearVisible);
  }, [comandearVisible, onComandearVisibleChange]);

  // Panel lateral: alto propio con scroll interno, para que la barra de total y
  // acciones quede al pie del panel y la lista corra por dentro.
  const isPanel = embedded && !comandearVisible;

  // Tablet y escritorio angosto (768–1199): el detalle toma la pantalla pero no
  // hay panel. Antes caía en el layout de dos columnas y la barra del total
  // quedaba flotando en un hueco muerto a la derecha. A pantalla completa lo
  // que corresponde es una sola columna con la barra al pie.
  const isStacked = !isPanel && !isMobile && !comandearVisible;

  // Alto real de la barra fija de móvil, medido en vivo: es lo que hay que
  // reservar debajo de la lista para que no le tape el último renglón.
  const bottomBarRef = useRef<HTMLDivElement | null>(null);
  const [bottomBarHeight, setBottomBarHeight] = useState(140);
  useEffect(() => {
    const el = bottomBarRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setBottomBarHeight(el.getBoundingClientRect().height);
    });
    observer.observe(el);
    setBottomBarHeight(el.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, [isMobile, isStacked, comandearVisible]);

  // Bordes de la barra fija fuera del teléfono. Se miden contra el contenedor
  // de la página en vez de usar left/right 0: en tablet y escritorio angosto
  // hay riel de sidebar y la página va centrada con un ancho máximo, así que
  // una barra a todo lo ancho se metería debajo del riel.
  const pageRef = useRef<HTMLDivElement | null>(null);
  const [barInset, setBarInset] = useState<{ left: number; right: number } | null>(null);
  useLayoutEffect(() => {
    if (!isStacked) {
      setBarInset(null);
      return;
    }
    const measure = () => {
      const el = pageRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setBarInset({ left: rect.left, right: window.innerWidth - rect.right });
    };
    measure();
    window.addEventListener('resize', measure);
    const el = pageRef.current;
    const observer = el ? new ResizeObserver(measure) : null;
    if (el && observer) observer.observe(el);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [isStacked]);

  // Esta pantalla se monta por estado dentro de /tables, así que el layout no
  // puede deducir por la URL que aquí abajo hay una barra fija. Al declararlo,
  // la tab bar de modo piso se retira y el botón del menú vuelve al header; sin
  // esto la barra de acciones la tapaba (z-index 100 contra 90) y el mesero se
  // quedaba sin ninguna forma de navegar.
  //
  // Se reclama por toda la pantalla en móvil y no sólo mientras se ve la barra:
  // en el otro sub-estado, comandear ocupa el alto completo y la tab bar le
  // taparía el final del panel. Es una pantalla de flujo, no un destino.
  useOwnsBottomEdge(isMobile);
  const [precuentaVisible, setPrecuentaVisible] = useState(false);
  const [paymentVisible, setPaymentVisible] = useState(false);
  const [splitVisible, setSplitVisible] = useState(false);
  const [discountVisible, setDiscountVisible] = useState(false);
  // Renglón al que se le está aplicando cortesía/descuento (null = modal cerrado)
  const [itemDiscountTarget, setItemDiscountTarget] = useState<OrderItemType | null>(null);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [actionsDrawerVisible, setActionsDrawerVisible] = useState(false);
  const [orderHistoryVisible, setOrderHistoryVisible] = useState(false);
  const [tableHistoryVisible, setTableHistoryVisible] = useState(false);
  const [guestsVisible, setGuestsVisible] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [resendModalVisible, setResendModalVisible] = useState(false);
  const [dailyMenuActiveKey, setDailyMenuActiveKey] = useState<string | undefined>(undefined);
  const [addCoursesItem, setAddCoursesItem] = useState<OrderItemType | null>(null);

  // Kitchen order mutation (for reactivating selected items)
  const [updateKitchenOrderItemsStatus] = useUpdateKitchenOrderItemsStatusMutation();
  // Rescate de renglones que nunca tuvieron comanda (ver ResendOrderModal)
  const [comandearOrderItems] = useComandearOrderItemsMutation();

  // Incremental send for "Agregar tiempos faltantes"
  const { send: sendIncrementalDailyMenu } = useIncrementalDailyMenuSend();

  // Check if KDS is enabled (production centers or KDS setting)
  const { data: hasProductionCentersData } = useHasProductionCentersQuery();
  const { data: kdsEnabledSetting } = useGetBoolSettingQuery('KitchenDisplayEnabled');
  const { data: printComandasSetting } = useGetBoolSettingQuery('ImprimirComandas');
  const { data: cloudPrintSetting } = useGetBoolSettingQuery('CloudPrintEnabled');
  // Nube solo con servidor y con la orden ya en él: ver shouldUseCloudPrint.
  const cloudPrintSettingOn = cloudPrintSetting?.value ?? false;
  // Whether the autofactura QR should be printed on precuenta tickets (default true)
  const { data: qrPrecuentaSetting } = useGetBoolSettingQuery('AutofacturaQr_Precuenta');
  const qrEnPrecuenta = qrPrecuentaSetting?.value ?? true;
  const { printKitchenOrder: cloudPrintKitchenOrder, printJob: cloudPrintJob } = useCloudPrint();
  useGetProductsQuery({ availableOnly: false });
  const hasProductionCenters = hasProductionCentersData?.hasProductionCenters ?? false;
  // If production centers exist -> always send to KDS
  // If no production centers -> only send to KDS if KitchenDisplayEnabled = true
  const shouldSendToKds = hasProductionCenters || (kdsEnabledSetting?.value ?? false);
  const shouldPrintComandas = printComandasSetting?.value ?? true;

  // Check user permissions based on role
  const userRole = user?.role?.toLowerCase();
  const canComandar = userRole !== 'cajero'; // Cajeros NO pueden comandear (pero Cajero/Mesero sí puede)
  // Espejo del gate del backend (PaymentFlowRules.IsCashierRole) vía helper
  // compartido: la lista de roles vivía duplicada aquí y en OrdersList con
  // criterios distintos, y ninguna coincidía con el backend.
  const canCobrar = canProcessPayments(user?.role);
  // Descontar baja el total de la cuenta, así que va con los que cobran. Esta acción no
  // tenía gate ninguno: la veía y la usaba cualquiera, incluido el Mesero.
  const canDescontar = canApplyDiscounts(user?.role);

  // Handler for resending kitchen order — opens modal to select which comandas to resend
  const handleResendOrder = () => {
    if (!activeOrder) {
      message.error('No hay orden activa para reenviar');
      return;
    }
    setResendModalVisible(true);
  };

  // Handler for resend confirmation from ResendOrderModal
  const handleResendConfirm = async (
    selectedKitchenOrders: KitchenOrderDto[],
    selectedItemIds: Set<string>,
    selectedMissingItemIds: Set<string> = new Set(),
  ) => {
    if (!activeOrder || !table) return;
    setResendModalVisible(false);
    setPrinting(true);

    try {
      // Renglones que nunca tuvieron comanda: aquí no hay nada que reactivar, hay que
      // crearla. El backend la arma leyendo lo persistido y es idempotente.
      //
      // Lo que devuelve entra a la cola de impresión junto con lo demás: sin eso, en una
      // cocina que trabaja con papel el rescate llegaría al KDS y no saldría ticket.
      let rescuedItems: KdsOrderItemDto[] = [];

      if (selectedMissingItemIds.size > 0) {
        try {
          const created = await comandearOrderItems({
            orderId: activeOrder.id,
            orderItemIds: Array.from(selectedMissingItemIds),
          }).unwrap();

          if (created.orders.length > 0) {
            rescuedItems = created.orders.flatMap(ko => ko.items.filter(i => !i.isCancelled));
            message.success(
              `${selectedMissingItemIds.size} producto(s) sin comanda enviado(s) a cocina`,
            );
          } else {
            // Ni error ni envío: el gate del KDS está apagado, o alguien más ya los mandó.
            // Decir "enviado" aquí es justo la mentira que este PR vino a quitar.
            message.info(
              'Los productos sin comanda no se enviaron al KDS (revisa si el envío a cocina está habilitado).',
              8,
            );
          }
        } catch (err) {
          console.error('Failed to comandear missing order items:', err);
          message.error(
            'No se pudo enviar a cocina los productos sin comanda. Vuelve a intentarlo o avisa a cocina directamente.',
            8,
          );
        }
      }

      // Collect only selected items from kitchen orders, grouping duplicates
      const rawItems = [
        ...selectedKitchenOrders.flatMap(ko =>
          ko.items.filter(i => !i.isCancelled && selectedItemIds.has(i.id))
        ),
        ...rescuedItems,
      ];
      const groupMap = new Map<string, { orderItemId?: string; productName: string; quantity: number; variants?: string; notes?: string }>();
      for (const item of rawItems) {
        const key = `${item.productName}|${item.variants || ''}|${item.notes || ''}`;
        const existing = groupMap.get(key);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          groupMap.set(key, {
            orderItemId: item.orderItemId || undefined,
            productName: item.productName,
            quantity: item.quantity,
            variants: item.variants || undefined,
            notes: item.notes || undefined,
          });
        }
      }
      const allKitchenItems = Array.from(groupMap.values());

      // Reactivate selected items back to Pending so they reappear on KDS
      if (shouldSendToKds) {
        for (const ko of selectedKitchenOrders) {
          const itemIdsToReactivate = ko.items
            .filter(i => selectedItemIds.has(i.id))
            .map(i => i.id);
          if (itemIdsToReactivate.length > 0) {
            try {
              await updateKitchenOrderItemsStatus({
                id: ko.id,
                kitchenOrderItemIds: itemIdsToReactivate,
                status: KitchenOrderStatus.Pending,
              }).unwrap();
            } catch (err) {
              console.warn('Failed to reactivate kitchen order items:', ko.id, err);
            }
          }
        }
      }

      // Print the comanda
      if (shouldPrintComandas && allKitchenItems.length > 0) {
        // Convert grouped items to print format
        const printItems = allKitchenItems.map(item => ({
          id: '',
          orderId: activeOrder.id,
          productId: '',
          productName: item.productName,
          quantity: item.quantity,
          unitPrice: 0,
          total: 0,
          variants: item.variants
            ? [{ id: '', orderItemId: '', variantId: '', variantName: item.variants, priceModifier: 0 }]
            : [],
          notes: item.notes,
          isCancelled: false,
        }));

        const tableLabel = `${table.areaName} - ${table.number}`;

        if (shouldUseCloudPrint(cloudPrintSettingOn, activeOrder)) {
          await cloudPrintKitchenOrder({
            orderId: activeOrder.id,
            tableLabel,
            waiterName: activeOrder.assignedWaiterName || '',
            items: printItems.map(item => ({
              productId: item.productId,
              productName: item.productName,
              quantity: item.quantity,
              variants: item.variants ? item.variants.map((v: any) => v.variantName || v) : [],
              notes: item.notes,
            })),
          });
          message.success(`${printItems.length} producto(s) reimpreso(s)`);
        } else {
        const printAgentAvailable = await printAgentService.isAvailable();
        const bluetoothAvailable = bluetoothPrintService.isAvailable();

        if (printAgentAvailable) {
          const result = await printAgentService.printKitchenOrder(
            activeOrder, tableLabel, activeOrder.assignedWaiterName || '', printItems as any
          );
          if (result.success) {
            message.success(`${printItems.length} producto(s) reimpreso(s)`);
          } else {
            message.error(`Error al reimprimir: ${result.message}`);
          }
        } else if (bluetoothAvailable) {
          const result = await bluetoothPrintService.printKitchenOrder(
            activeOrder, tableLabel, activeOrder.assignedWaiterName || '', printItems as any
          );
          if (result.success) {
            message.success(`${printItems.length} producto(s) reimpreso(s)`);
          } else {
            message.error(`Error al reimprimir: ${result.message}`);
          }
        } else {
          message.warning('No hay servicio de impresión disponible');
        }
        }
      }
    } catch (error: any) {
      console.error('Error resending order:', error);
      message.error('Error al reenviar la comanda');
    } finally {
      setPrinting(false);
    }
  };

  // Handler for reprinting precheck
  const handleReprintPreCheck = async () => {
    if (!activeOrder) {
      message.error('No hay orden activa para reimprimir precuenta');
      return;
    }

    setPrinting(true);
    try {
      // Check which print services are available
      const printAgentAvailable = await printAgentService.isAvailable();
      const bluetoothAvailable = bluetoothPrintService.isAvailable();

      // Build autofactura QR URL if enabled (and allowed on precuenta). Nunca en una orden
      // tomada sin conexión: su número es 0 hasta sincronizar (ver PreCheckModal).
      const autofacturaUrl = qrEnPrecuenta && facturacionConfig?.autofacturaEnabled && facturacionConfig?.autofacturaCode
        && !activeOrder.offlineFolioLabel
        ? `${window.location.origin}/autofactura/${facturacionConfig.autofacturaCode}?order=${activeOrder.orderNumber}&total=${total.toFixed(2)}`
        : undefined;

      const preview = buildOrderTicketPreview({
        kind: 'precuenta',
        title: 'PRECUENTA',
        tableLabel: `Mesa ${table?.number}`,
        waiterName: activeOrder.assignedWaiterName || undefined,
        orderLabel: formatOrderNumber(activeOrder),
        items: activeOrder.orderItems || [],
        subtotal,
        tax,
        discounts: appliedDiscounts,
        total,
      });

      if (shouldUseCloudPrint(cloudPrintSettingOn, activeOrder)) {
        // Field names must match the backend EscPosReceiptData DTO exactly
        // (JSON binding is case-insensitive but does not translate names).
        // DateTime is intentionally omitted: the backend stamps it in the
        // restaurant's timezone. See CloudPrintJobService.EnqueueAsync.
        await withPrintAnimation(preview, () => cloudPrintJob({
          printType: 'PreCheck',
          receiptData: {
            restaurantName: restaurantSettings?.name || 'MASSRESTAURANT',
            address: restaurantSettings?.address,
            phone: restaurantSettings?.phone,
            title: 'PRE-CUENTA',
            tableName: `Mesa ${table?.number}`,
            waiterName: activeOrder.assignedWaiterName || 'Sin asignar',
            items: (activeOrder.orderItems || []).filter((i: any) => !i.isCancelled).map((i: any) => ({
              quantity: i.quantity,
              name: i.productName,
              unitPrice: i.unitPrice,
              total: i.total,
              variants: (i.variants || []).map((v: any) => v.variantName),
              notes: i.notes,
            })) || [],
            subtotal,
            tax,
            taxRate,
            discounts: appliedDiscounts.map(d => ({ reason: d.reason || 'Descuento', amount: d.discountAmount })),
            total,
            autofacturaUrl,
          },
        }));
        message.success('Precuenta enviada a imprimir');
      }
      // Try Print Agent (Windows)
      else if (printAgentAvailable) {
        const result = await withPrintAnimation(preview, () => printAgentService.printPreCheck(
          activeOrder,
          restaurantSettings?.name || 'MASSRESTAURANT',
          restaurantSettings?.address,
          restaurantSettings?.phone,
          `Mesa ${table?.number}`,
          activeOrder.assignedWaiterName || 'Sin asignar',
          subtotal,
          tax,
          taxRate,
          appliedDiscounts,
          total,
          autofacturaUrl
        ));

        if (result.success) {
          message.success('Precuenta reimpresa correctamente');
        } else {
          message.error(`Error al reimprimir: ${result.message}`);
          console.error('Print Agent error:', result.error);
        }
      }
      // Try Bluetooth if Print Agent not available (Android/Chrome)
      else if (bluetoothAvailable) {
        const result = await withPrintAnimation(preview, () => bluetoothPrintService.printPreCheck(
          activeOrder,
          restaurantSettings?.name || 'MASSRESTAURANT',
          restaurantSettings?.address,
          restaurantSettings?.phone,
          `Mesa ${table?.number}`,
          activeOrder.assignedWaiterName || 'Sin asignar',
          subtotal,
          tax,
          taxRate,
          appliedDiscounts,
          total,
          autofacturaUrl
        ));

        if (result.success) {
          message.success('Precuenta reimpresa correctamente');
        } else {
          message.error(`Error al reimprimir: ${result.message}`);
          console.error('Bluetooth error:', result.error);
        }
      }
      // No print service available
      else {
        message.warning('No hay servicio de impresión disponible');
        console.warn('No print service available');
      }
    } catch (error: any) {
      console.error('Error reprinting precheck:', error);
      message.error('Error al reimprimir la precuenta');
    } finally {
      setPrinting(false);
    }
  };

  // Get active order for this table
  const { 
    data: activeOrder, 
    isLoading: orderLoading,
    error: orderError,
    refetch: refetchOrder
  } = useGetActiveOrderByTableQuery(table?.id || '');

  // Get discounts for this order
  const {
    data: orderDiscounts,
    refetch: refetchDiscounts
  } = useGetOrderDiscountsQuery(activeOrder?.id || '', {
    skip: !activeOrder?.id
  });

  // Get restaurant settings for tax rate
  const { data: restaurantSettings } = useGetRestaurantSettingsQuery();
  const { data: facturacionConfig } = useGetFacturacionConfigQuery();
  const taxRate = (restaurantSettings?.taxRate || 16) / 100; // Convert percentage to decimal

  // Calculate open time and duration from active order
  const getOpenTimeInfo = () => {
    if (!activeOrder?.openTime) return null;

    const openDateTime = new Date(activeOrder.openTime);
    const now = new Date();
    const durationMs = now.getTime() - openDateTime.getTime();

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    const openTime = formatInTimeZone(activeOrder.openTime, 'HH:mm');
    const duration = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

    return { openTime, duration };
  };

  const openTimeInfo = getOpenTimeInfo();

  // Calculate totals from order
  const orderItems = activeOrder?.orderItems || [];

  // NOTE: Total price is used as base for tax calculation
  // Tax is calculated as a percentage of the total, then subtracted to get subtotal
  const totalWithTax = orderItems.reduce((sum, item) => {
    if (item.isCancelled) return sum;
    return sum + item.total; // item.total already includes IVA
  }, 0);

  // Extract tax from total (prices already include IVA)
  // Subtotal = Total / (1 + taxRate), Tax = Total - Subtotal
  const subtotal = totalWithTax / (1 + taxRate);
  const tax = totalWithTax - subtotal;
  const totalBeforeDiscount = totalWithTax;

  // Calculate discount amount from applied discounts. Los de producto van
  // etiquetados ("Cortesía (Café)") para la barra de total y los tickets.
  const appliedDiscounts = withDiscountLabels(orderDiscounts || [], orderItems);
  const itemDiscounts = itemDiscountsById(orderDiscounts);
  const totalDiscountAmount = appliedDiscounts.reduce((sum, discount) => sum + discount.discountAmount, 0);
  
  // Al centavo: con todo en cortesía la resta deja un residuo y `canForceClose`
  // (total === 0) nunca se habilitaba.
  const total = Math.max(roundCents(totalBeforeDiscount - totalDiscountAmount), 0);


  const handleAction = async (action: () => Promise<void> | void) => {
    setLoading(true);
    try {
      await action();
    } catch (error) {
      console.error('Error al ejecutar acción:', error);
      message.error('Error al ejecutar acción');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelItem = async (orderItemId: string) => {
    try {
      await onCancelOrderItem?.(orderItemId, activeOrder?.id);
      message.success('Producto cancelado exitosamente');
      refetchOrder();
    } catch (error) {
      message.error('Error al cancelar producto');
    }
  };

  const handleReopenTable = async () => {
    try {
      await onReopenTable?.(table.id);
      message.success('Mesa reabierta exitosamente');
      
      // Refresh data after successful reopen
      onTableUpdate?.();
      refetchOrder();
    } catch (error) {
      message.error('Error al reabrir mesa');
    }
  };

  const getStatusColor = () => {
    switch (table.status) {
      case 'Occupied': return '#f5222d';
      case 'PreCheck': return '#faad14';
      case 'Dirty': return '#8c8c8c';
      default: return '#d9d9d9';
    }
  };

  const getStatusText = () => {
    switch (table.status) {
      case 'Occupied': return 'Ocupada';
      case 'PreCheck': return 'Precuenta';
      case 'Dirty': return 'Sucia';
      default: return table.status;
    }
  };

  // Salida compartida por el cobro manual y el de terminal: los dos chocan con
  // el mismo rechazo del backend cuando la orden ya está dividida.
  const goToSplitPayment = () => {
    setPaymentVisible(false);
    setSplitVisible(true);
    refetchOrder();
  };

  const isPreCheck = table.status === 'PreCheck';
  const hasActiveOrder = activeOrder && activeOrder.orderItems && activeOrder.orderItems.length > 0;
  // Orden con cuenta dividida: se cobra por cuentas (el backend rechaza el
  // cobro completo, agregar/cancelar productos y descuentos de orden)
  const isSplitOrder = !!activeOrder?.isSplit;
  // Orden tomada por comensal: los renglones traen C1/C2. Es la señal de que
  // dividir es el camino esperado y no un caso raro.
  const hasSeatedItems = (activeOrder?.orderItems || []).some(i => !i.isCancelled && i.seatNumber);
  const canForceClose = total === 0;

  // Merged-tables group: the primary shows the combined seats and which
  // tables share the account.
  const totalCapacity = table.capacity +
    (table.mergedTables?.reduce((sum, t) => sum + t.capacity, 0) ?? 0);
  const mergedNumbers = table.mergedTables?.map(t => t.number).join(', ');

  // Con 0 y 0 (orden vieja o sincronizada sin el dato) también se muestra: es
  // justo la que más hace falta corregir.
  const comensalesText = formatGuests(activeOrder?.adults, activeOrder?.children) || 'Sin comensales';

  // Cancelling an item is destructive: it lives behind the ⋯ menu of each row
  // and always asks for confirmation.
  const confirmCancelItem = (item: OrderItemType) => {
    modal.confirm({
      title: `¿Cancelar ${item.quantity}x ${item.dailyMenuName || item.productName}?`,
      content: 'Esta acción no se puede deshacer',
      okText: 'Sí, cancelar',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => handleCancelItem(item.id),
    });
  };

  // Mover un renglón de comensal es cambiar una etiqueta: no toca dinero ni
  // cocina, así que va sin confirmación. Bloqueado solo con la cuenta dividida.
  const [setOrderItemSeat] = useSetOrderItemSeatMutation();
  const handleChangeSeat = async (item: OrderItemType, seatNumber: number | null) => {
    if (!activeOrder) return;
    try {
      await setOrderItemSeat({ orderId: activeOrder.id, itemId: item.id, seatNumber }).unwrap();
      refetchOrder();
    } catch (error: any) {
      message.error(error?.data?.message || 'No se pudo cambiar el comensal');
    }
  };
  // Quitar un descuento de producto sube la cuenta: confirma, igual que el
  // Popconfirm del modal de descuentos.
  const [removeDiscount] = useRemoveDiscountMutation();
  const confirmRemoveItemDiscount = (item: OrderItemType) => {
    const discount = itemDiscounts[item.id];
    if (!discount) return;
    modal.confirm({
      title: `¿Quitar el descuento de ${item.dailyMenuName || item.productName}?`,
      content: `${itemDiscountTag(discount, item)}: la cuenta vuelve a cobrar $${discount.discountAmount.toFixed(2)} por este producto.`,
      okText: 'Quitar',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await removeDiscount(discount.id).unwrap();
          message.success('Descuento quitado');
          refetchDiscounts();
          refetchOrder();
        } catch (error: any) {
          message.error(error?.data?.message || 'No se pudo quitar el descuento');
        }
      },
    });
  };

  // Precio del renglón. Con descuento de producto: el original tachado, lo que
  // queda y la etiqueta (Cortesía, -20%), para que la mesa se lea sin abrir nada.
  const renderItemPrice = (item: OrderItemType) => {
    const fontSize = isMobile ? '13px' : '14px';
    const discount = !item.isCancelled ? itemDiscounts[item.id] : undefined;
    if (!discount) {
      return (
        <Text strong style={{ fontSize, color: '#1890ff' }}>
          ${item.total.toFixed(2)}
        </Text>
      );
    }
    return (
      <Space size={6} wrap>
        <Text delete type="secondary" style={{ fontSize }}>
          ${item.total.toFixed(2)}
        </Text>
        <Text strong style={{ fontSize, color: '#1890ff' }}>
          ${netItemTotal(item, itemDiscounts).toFixed(2)}
        </Text>
        <Tag color="orange" style={{ marginInlineEnd: 0 }}>
          {itemDiscountTag(discount, item)}
        </Tag>
      </Space>
    );
  };

  const seatOptionsCount = Math.min(
    MAX_SEATS,
    Math.max((activeOrder?.adults ?? 0) + (activeOrder?.children ?? 0), maxSeatIn(activeOrder?.orderItems ?? []), 1) + 1
  );

  const itemRowMenu = (item: OrderItemType) => (
    <Dropdown
      trigger={['click']}
      menu={{
        items: [
          ...(!isSplitOrder ? [{
            key: 'seat',
            icon: <TeamOutlined />,
            label: item.seatNumber ? `Comensal ${seatLabel(item.seatNumber)}` : 'Comensal',
            children: [
              {
                key: 'seat-shared',
                label: 'Compartido (todos)',
                disabled: !item.seatNumber,
                onClick: () => handleChangeSeat(item, null),
              },
              ...Array.from({ length: seatOptionsCount }, (_, i) => i + 1).map(seat => ({
                key: `seat-${seat}`,
                label: seatLabel(seat),
                disabled: item.seatNumber === seat,
                onClick: () => handleChangeSeat(item, seat),
              })),
            ],
          }] : []),
          // Mismo gate que el descuento a la cuenta (rol de caja) y el mismo
          // bloqueo con la cuenta dividida: el backend rechaza ambos.
          ...(canDescontar && !isSplitOrder ? [
            itemDiscounts[item.id]
              ? {
                  key: 'discount-remove',
                  icon: <GiftOutlined />,
                  label: 'Quitar descuento',
                  onClick: () => confirmRemoveItemDiscount(item),
                }
              : {
                  key: 'discount',
                  icon: <GiftOutlined />,
                  label: 'Cortesía o descuento',
                  onClick: () => setItemDiscountTarget(item),
                },
          ] : []),
          {
            key: 'cancel',
            danger: true,
            icon: <DeleteOutlined />,
            label: 'Cancelar producto',
            onClick: () => confirmCancelItem(item),
          },
        ],
      }}
    >
      <Button
        type="text"
        size="small"
        icon={<MoreOutlined />}
        aria-label="Opciones del producto"
        onClick={(e) => e.stopPropagation()}
      />
    </Dropdown>
  );

  // --- Actions ranked by table state: primary (big buttons on top; bottom
  // bar on mobile), secondary (uniform outline) and "more" (histories). ---
  const primaryActions: TableAction[] = isPreCheck
    ? [
        ...(canCobrar && hasActiveOrder ? [{
          key: 'liquidar',
          // En el teléfono la etiqueta larga no cabe junto a Reabrir Mesa.
          label: isSplitOrder ? 'Cobrar Cuentas' : (isMobile ? 'Liquidar' : 'Liquidar Cuenta'),
          icon: <CreditCardOutlined />,
          buttonType: 'primary' as const,
          style: { backgroundColor: '#52c41a', borderColor: '#52c41a' },
          onClick: () => (isSplitOrder ? setSplitVisible(true) : setPaymentVisible(true)),
        }] : []),
        {
          key: 'reabrir',
          label: 'Reabrir Mesa',
          icon: <PlayCircleOutlined />,
          buttonType: 'primary' as const,
          style: { backgroundColor: '#faad14', borderColor: '#faad14' },
          onClick: handleReopenTable,
        },
      ]
    : [
        ...(canComandar && !isSplitOrder ? [{
          key: 'comandear',
          label: 'Comandear',
          icon: <EditOutlined />,
          buttonType: 'primary' as const,
          onClick: () => setComandarVisible(true),
        }] : []),
        ...(isSplitOrder && canCobrar ? [{
          key: 'cobrar-cuentas',
          label: 'Cobrar Cuentas',
          icon: <CreditCardOutlined />,
          buttonType: 'primary' as const,
          style: { backgroundColor: '#52c41a', borderColor: '#52c41a' },
          onClick: () => setSplitVisible(true),
        }] : []),
        // La orden que se tomó por comensal se va a dividir casi seguro: el
        // botón sube junto a los demás en vez de quedar sepultado en "Más
        // acciones". Sin comensales el flujo normal es cobrar completo y ahí
        // se queda como estaba.
        ...(hasSeatedItems && canCobrar && !isSplitOrder ? [{
          key: 'dividir',
          // En el teléfono la etiqueta larga no cabe junto a las otras dos.
          label: isMobile ? 'Dividir' : 'Dividir Cuenta',
          icon: <ScissorOutlined />,
          buttonType: 'primary' as const,
          onClick: () => setSplitVisible(true),
        }] : []),
        ...(hasActiveOrder ? [{
          key: 'precuenta',
          label: 'Precuenta',
          icon: <PrinterOutlined />,
          buttonType: 'primary' as const,
          style: { backgroundColor: '#faad14', borderColor: '#faad14' },
          onClick: () => setPrecuentaVisible(true),
        }] : []),
      ];

  const secondaryActions: TableAction[] = [
    ...(isPreCheck && hasActiveOrder ? [{
      key: 'reimprimir-precuenta',
      label: 'Reimprimir Precuenta',
      icon: <PrinterOutlined />,
      loading: printing,
      onClick: handleReprintPreCheck,
    }] : []),
    ...(!isPreCheck && hasActiveOrder && canCobrar && !isSplitOrder ? [{
      key: 'liquidar',
      label: isMobile ? 'Liquidar' : 'Liquidar Cuenta',
      icon: <CreditCardOutlined />,
      onClick: () => setPaymentVisible(true),
    }] : []),
    // Con comensales ya subió a las acciones principales; aquí sería doble.
    ...(hasActiveOrder && canCobrar && !isSplitOrder && !(hasSeatedItems && !isPreCheck) ? [{
      key: 'dividir',
      label: 'Dividir Cuenta',
      icon: <ScissorOutlined />,
      onClick: () => setSplitVisible(true),
    }] : []),
    ...(!isPreCheck && hasActiveOrder ? [{
      key: 'reenviar',
      label: 'Reenviar Comanda',
      icon: <PrinterOutlined />,
      loading: printing,
      onClick: handleResendOrder,
    }] : []),
    ...(hasActiveOrder && !isSplitOrder && canDescontar ? [{
      key: 'descuentos',
      label: 'Aplicar Descuentos',
      icon: <PercentageOutlined />,
      onClick: () => setDiscountVisible(true),
    }] : []),
    ...(hasActiveOrder ? [{
      key: 'transferir',
      label: 'Transferir Mesa',
      icon: <SwapOutlined />,
      onClick: () => onTransferTable?.(table),
    }] : []),
  ];

  const moreActions: TableAction[] = [
    ...(hasActiveOrder ? [{
      key: 'historial-orden',
      label: 'Historial de Orden',
      icon: <HistoryOutlined />,
      onClick: () => setOrderHistoryVisible(true),
    }] : []),
    {
      key: 'historial-mesa',
      label: 'Historial de Mesa',
      icon: <HistoryOutlined />,
      onClick: () => setTableHistoryVisible(true),
    },
  ];

  const renderActionButton = (
    action: TableAction,
    opts?: { closeDrawer?: boolean; grow?: boolean; size?: 'large' | 'middle'; basis?: string }
  ) => (
    <Button
      key={action.key}
      type={action.buttonType || 'default'}
      icon={action.icon}
      size={opts?.size || 'large'}
      block={!opts?.grow}
      loading={action.loading}
      style={{
        ...(action.style || {}),
        // `flex: 1 1 120px` en vez de `flex: 1`: con base 0 los botones no
        // pedían ancho y la fila se desbordaba antes de decidir envolver.
        ...(opts?.grow ? { flex: `1 1 ${opts.basis || '120px'}`, minWidth: 0 } : {}),
      }}
      onClick={() => {
        action.onClick();
        if (opts?.closeDrawer) setActionsDrawerVisible(false);
      }}
    >
      {action.label}
    </Button>
  );

  // Escritorio: todo lo que no es acción primaria vive en un menú que abre HACIA
  // ARRIBA desde la barra fija. Hacia abajo se saldría de la pantalla, porque la
  // barra está pegada al borde inferior.
  const confirmForceClose = () => {
    modal.confirm({
      title: '¿Cerrar mesa forzadamente?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esta acción cerrará la mesa.',
      okText: 'Sí, cerrar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () =>
        handleAction(async () => {
          if (onCloseTable) {
            await onCloseTable(table.id);
          } else {
            message.error('Función de cerrar mesa no está disponible');
          }
        }),
    });
  };

  // `disabled: action.loading` NO es cosmético: es la guarda de re-entrada que
  // el botón traía de serie. Reimprimir Precuenta y Reenviar Comanda no tienen
  // guarda propia, así que sin esto un doble clic manda dos trabajos de
  // impresión a la cocina.
  const actionsMenuItems: MenuProps['items'] = [
    ...secondaryActions.map(action => ({
      key: action.key,
      icon: action.icon,
      label: action.label,
      disabled: action.loading,
      onClick: action.onClick,
    })),
    ...(secondaryActions.length > 0 && moreActions.length > 0
      ? [{ type: 'divider' as const, key: 'div-more' }]
      : []),
    ...moreActions.map(action => ({
      key: action.key,
      icon: action.icon,
      label: action.label,
      disabled: action.loading,
      onClick: action.onClick,
    })),
    { type: 'divider' as const, key: 'div-danger' },
    {
      key: 'cerrar-forzar',
      icon: <CloseOutlined />,
      label: 'Cerrar Mesa (Forzar)',
      danger: true,
      disabled: !canForceClose,
      title: !canForceClose
        ? 'Solo se puede cerrar forzadamente si la cuenta está en ceros ($0.00)'
        : undefined,
      onClick: confirmForceClose,
    },
  ];

  // Barra fija: total + acciones en un mismo bloque. El subtotal y el IVA no se
  // muestran aquí a propósito — al comandear sólo importa el total; el desglose
  // vive en la precuenta y en el cobro.
  const totalAndActionsBar = (
    <>
      {appliedDiscounts.length > 0 && (
        <Row justify="space-between" align="middle" style={{ marginBottom: 6 }}>
          <Text style={{ fontSize: isMobile ? '11px' : '12px', color: '#ff4d4f' }}>
            {appliedDiscounts.length === 1
              ? `Descuento (${appliedDiscounts[0].reason})`
              : `${appliedDiscounts.length} descuentos aplicados`}
          </Text>
          <Text style={{ fontSize: isMobile ? '11px' : '12px', color: '#ff4d4f' }}>
            -${totalDiscountAmount.toFixed(2)}
          </Text>
        </Row>
      )}
      <Row justify="space-between" align="middle" style={{ marginBottom: isMobile ? 8 : 12 }}>
        <Text
          type="secondary"
          strong
          style={{ fontSize: isMobile ? '11px' : '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          Total a pagar
        </Text>
        <Text
          strong
          style={{
            fontSize: isMobile ? '20px' : '26px',
            color: 'var(--color-success-text)',
            lineHeight: 1.1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          ${total.toFixed(2)}
        </Text>
      </Row>
      {/* Envuelve: en precuenta salen tres acciones primarias y en un panel
          angosto una fila rígida se desbordaba fuera de la tarjeta.

          En el teléfono, con tres acciones (mesa tomada por comensal:
          Comandear · Dividir · Precuenta) no cabe una cuarta cosa en el
          renglón y el botón de "más acciones" caía solo a la fila de abajo.
          Con base de media fila quedan dos parejas arriba y la tercera
          compartiendo renglón con los tres puntos. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {primaryActions.map(action =>
          renderActionButton(action, {
            grow: true,
            basis: isMobile && primaryActions.length >= 3 ? 'calc(50% - 4px)' : undefined,
          })
        )}
        {isMobile ? (
          <Button
            size="large"
            icon={<MoreOutlined />}
            onClick={() => setActionsDrawerVisible(true)}
            aria-label="Más acciones"
            style={{ flexShrink: 0 }}
          />
        ) : (
          <Dropdown
            trigger={['click']}
            placement="topRight"
            menu={{ items: actionsMenuItems }}
          >
            {/* En panel va sólo el icono: la etiqueta se come el ancho que
                necesitan las acciones primarias. */}
            <Button
              size="large"
              icon={<MoreOutlined />}
              aria-label={isPanel ? 'Más acciones' : undefined}
              style={{ flexShrink: 0 }}
            >
              {isPanel ? '' : 'Más acciones'}
            </Button>
          </Dropdown>
        )}
      </div>
    </>
  );

  const forceCloseButton = (closeDrawer?: boolean) => (
    <Popconfirm
      title="¿Cerrar mesa forzadamente?"
      description={canForceClose ? "Esta acción cerrará la mesa" : "Solo se puede cerrar forzadamente si la cuenta está en ceros"}
      onConfirm={() => {
        handleAction(async () => {
          if (onCloseTable) {
            await onCloseTable(table.id);
          } else {
            message.error('Función de cerrar mesa no está disponible');
          }
        });
        if (closeDrawer) setActionsDrawerVisible(false);
      }}
      okText="Sí, cerrar"
      cancelText="Cancelar"
      okType="danger"
      disabled={!canForceClose}
    >
      <Button
        danger
        icon={<CloseOutlined />}
        size="large"
        block
        loading={loading}
        disabled={!canForceClose}
        title={!canForceClose ? "Solo se puede cerrar forzadamente si la cuenta está en ceros ($0.00)" : undefined}
      >
        Cerrar Mesa (Forzar)
      </Button>
    </Popconfirm>
  );

  return (
    <div ref={pageRef} className={isPanel ? 'table-panel' : undefined} style={isPanel ? {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      padding: 16,
      backgroundColor: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: 12,
    } : {
      padding: comandearVisible ? 0 : (isMobile ? '8px' : '16px'),
      // Móvil: hueco para la barra fija (total + acciones). Se mide en vivo —
      // fijarlo a ojo ya falló dos veces, porque la barra crece con la fila de
      // descuentos y con el safe area del home indicator, y el último renglón
      // del consumo quedaba tapado.
      paddingBottom: comandearVisible
        ? 0
        : ((isMobile || isStacked) ? `${bottomBarHeight + 12}px` : '16px'),
      maxWidth: comandearVisible ? '100%' : '1200px',
      // Embebido, el contenedor de la página ya dejó el Content a ras: los
      // márgenes negativos de comandear estaban dimensionados para cancelar un
      // padding de 24px que ahí ya no existe, y el panel se metía debajo del
      // header sticky y se desbordaba a los lados.
      margin: comandearVisible
        ? (embedded ? 0 : (isMobile ? '-8px -8px 0' : '-24px -24px 0'))
        : '0 auto',
      minHeight: embedded && comandearVisible ? 0 : '100%',
      height: comandearVisible
        ? (embedded ? '100%' : (isMobile ? 'calc(100vh - 70px)' : 'calc(100vh - 120px)'))
        : undefined,
      display: comandearVisible ? 'flex' : undefined,
      flexDirection: comandearVisible ? 'column' : undefined,
    }}>
      {comandearVisible ? (
        <ComandearContent
          table={table}
          visible={comandearVisible}
          onClose={() => {
            setComandarVisible(false);
            refetchOrder();
          }}
        />
      ) : (
      <>
      {/* Header with Back Button */}
      <div style={{
        marginBottom: isMobile ? '12px' : (isPanel ? '12px' : '24px'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        flexShrink: isPanel ? 0 : undefined,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          width: isMobile ? 'auto' : 'auto',
          justifyContent: 'flex-start',
          marginBottom: 0,
          order: isPanel ? 2 : undefined,
          flexShrink: 0,
        }}>
          {/* En panel las mesas siguen a la vista: no es "volver", es cerrar. */}
          <Button
            icon={isPanel ? <CloseOutlined /> : <ArrowLeftOutlined />}
            onClick={onBack}
            size={isMobile || isPanel ? 'small' : 'large'}
            type={isMobile || isPanel ? 'text' : 'default'}
            aria-label={isMobile || isPanel ? 'Volver a mesas' : undefined}
            style={{ marginRight: isMobile ? '2px' : (isPanel ? 0 : '16px') }}
          >
            {isMobile || isPanel ? '' : 'Volver a Mesas'}
          </Button>
        </div>

        <div style={{
          textAlign: isMobile || isPanel ? 'left' : 'center',
          flex: 1,
          width: 'auto',
          order: isPanel ? 1 : undefined,
          minWidth: 0,
        }}>
          {/* Móvil y panel: título, estado y hora en UN renglón. En el teléfono
              esto ahorra dos líneas completas sobre la lista de consumo. */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile || isPanel ? 8 : 12,
            flexWrap: isMobile || isPanel ? 'nowrap' : 'wrap',
            minWidth: 0,
          }}>
            <Title
              level={isMobile || isPanel ? 5 : 2}
              style={{
                margin: 0,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <EnvironmentOutlined style={{ marginRight: isMobile || isPanel ? '4px' : '8px' }} />
              Mesa {table.number}
            </Title>

            {/* En panel el estado ya se ve en la mesa seleccionada de la
                cuadrícula: repetirlo aquí sólo gasta el ancho que falta. */}
            {!isPanel && (
              <Tag
                color={getStatusColor()}
                style={{
                  fontSize: isMobile ? '10px' : '14px',
                  padding: isMobile ? '1px 6px' : '4px 12px',
                  margin: 0,
                  flexShrink: 0,
                }}
              >
                {getStatusText()}
              </Tag>
            )}
            {openTimeInfo && (
              <Text
                type="secondary"
                style={{
                  fontSize: isMobile || isPanel ? '11px' : '14px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                }}
              >
                <ClockCircleOutlined style={{ marginRight: '2px' }} />
                {isMobile
                  ? `${openTimeInfo.openTime} (${openTimeInfo.duration})`
                  : `Abierta desde ${openTimeInfo.openTime} (${openTimeInfo.duration})`}
              </Text>
            )}
          </div>

          {/* Table metadata inline — replaces the old "Información de Mesa" card */}
          <div style={{ marginTop: isMobile ? '2px' : '6px' }}>
            <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '14px' }}>
              {table.areaName} · {totalCapacity} personas
              {mergedNumbers ? ` · Unida con Mesa ${mergedNumbers}` : ''}
              {activeOrder?.assignedWaiterName ? ` · Mesero: ${activeOrder.assignedWaiterName}` : ''}
              {/* Solo cuando la abrió alguien distinto del mesero asignado */}
              {activeOrder?.userName && activeOrder.userName !== activeOrder.assignedWaiterName
                ? ` · Abrió: ${activeOrder.userName}`
                : ''}
              {/* Tocable: si llegó más gente o nadie movió el contador al abrir,
                  se corrige aquí. Es lo que cuentan el corte y Reportes. */}
              {activeOrder && (
                <>
                  {' · '}
                  <Typography.Link
                    onClick={() => setGuestsVisible(true)}
                    style={{ fontSize: 'inherit', whiteSpace: 'nowrap' }}
                    title="Corregir comensales"
                  >
                    {comensalesText} <EditOutlined />
                  </Typography.Link>
                </>
              )}
            </Text>
          </div>

          {/* Open-table note: visible up front, not buried in the history */}
          {activeOrder?.notes && (
            <div style={{ marginTop: isMobile ? '4px' : '8px' }}>
              <Alert
                type="info"
                showIcon
                icon={<MessageOutlined />}
                message={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>{activeOrder.notes}</span>}
                style={{
                  display: 'inline-flex',
                  padding: isMobile ? '4px 10px' : '6px 14px',
                  textAlign: 'left',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* En panel la fila es el área que scrollea: una sola columna, sin gutter
          (los márgenes negativos del gutter sacarían scroll horizontal). */}
      <Row
        className={isPanel ? 'table-panel__scroll' : undefined}
        gutter={isPanel ? 0 : (isMobile ? [8, 8] : [24, 24])}
        style={isPanel ? { flex: 1, minHeight: 0, overflowY: 'auto', marginBottom: 12 } : undefined}
      >
        {/* Una sola columna siempre: el detalle ocupa todo el ancho que tenga,
            sea el panel o la pantalla completa. */}
        <Col xs={24} lg={24}>
          {/* Consumption Details */}
          <Card 
            title={
              <Space size="small" wrap>
                <EditOutlined style={{ fontSize: isMobile ? '12px' : '16px' }} />
                <span style={{ fontSize: isMobile ? '12px' : '16px' }}>Detalles del Consumo</span>
                {hasActiveOrder && (
                  <Text type="secondary" style={{ fontSize: isMobile ? '9px' : '13px' }}>
                    (Orden {formatOrderNumber(activeOrder)})
                  </Text>
                )}
              </Space>
            }
            size={isMobile ? "small" : "default"}
            style={{ marginBottom: isMobile ? '8px' : '16px' }}
          >
            {orderLoading && (
              <Spin 
                size={isMobile ? "small" : "large"} 
                style={{ 
                  display: 'block', 
                  textAlign: 'center', 
                  padding: isMobile ? '12px' : '40px' 
                }} 
              />
            )}
            
            {orderError && (
              <div style={{ textAlign: 'center', padding: isMobile ? '12px' : '40px' }}>
                <Text type="danger" style={{ fontSize: isMobile ? '11px' : '14px' }}>
                  Error al cargar la orden
                </Text>
              </div>
            )}

            {!orderLoading && !orderError && !hasActiveOrder && (
              <div style={{ textAlign: 'center', padding: isMobile ? '12px' : '40px' }}>
                <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '14px' }}>
                  No hay consumo registrado
                </Text>
                <br />
                {canComandar && (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => setComandarVisible(true)}
                    style={{ marginTop: isMobile ? '8px' : '16px' }}
                    size={isMobile ? "small" : "large"}
                  >
                    Iniciar Comanda
                  </Button>
                )}
                {!canComandar && (
                  <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '12px', marginTop: '8px', display: 'block' }}>
                    Solo los meseros pueden iniciar comandas
                  </Text>
                )}
              </div>
            )}

            {!orderLoading && !orderError && hasActiveOrder && (
              <>
                {orderItems.some(i => !i.dailyMenuId) && (
                <List
                  dataSource={orderItems.filter(i => !i.dailyMenuId)}
                  size={isMobile ? "small" : "default"}
                  renderItem={(item, index) => (
                    <List.Item
                      key={item.id || index}
                      style={{
                        opacity: item.isCancelled ? 0.5 : 1,
                        backgroundColor: item.isCancelled ? 'var(--bg-error-light)' : 'transparent',
                        padding: isMobile ? '4px 0' : '12px 0'
                      }}
                      actions={!item.isCancelled ? [itemRowMenu(item)] : undefined}
                    >
                      <List.Item.Meta
                        title={
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            flexWrap: isMobile ? 'wrap' : 'nowrap'
                          }}>
                            <Space direction={isMobile ? "vertical" : "horizontal"} size="small">
                              <Text
                                strong={!item.isCancelled}
                                style={{
                                  textDecoration: item.isCancelled ? 'line-through' : 'none',
                                  fontSize: isMobile ? '13px' : '14px'
                                }}
                              >
                                {item.seatNumber && <SeatPill seatNumber={item.seatNumber} size="small" style={{ marginRight: 6 }} />}
                                {item.quantity}x {item.productName}
                              </Text>
                              {item.isCancelled && (
                                <Tag
                                  color="red"
                                  style={{
                                    fontSize: '10px',
                                    padding: '0 4px',
                                    lineHeight: '16px'
                                  }}
                                >
                                  CANCELADO
                                </Tag>
                              )}
                            </Space>
                          </div>
                        }
                        description={
                          <div style={{ marginTop: isMobile ? '4px' : '8px' }}>
                            {item.variants && item.variants.length > 0 && (
                              <Text
                                type="secondary"
                                style={{
                                  display: 'block',
                                  fontSize: isMobile ? '11px' : '13px',
                                  marginBottom: '4px'
                                }}
                              >
                                {item.variants.map(v => v.quantity ? `${v.quantity} ${v.variantName}` : v.variantName).join(', ')}
                              </Text>
                            )}
                            {item.notes && (
                              <Text
                                type="secondary"
                                italic
                                style={{
                                  display: 'block',
                                  fontSize: isMobile ? '11px' : '13px',
                                  marginBottom: '4px'
                                }}
                              >
                                Nota: {item.notes}
                              </Text>
                            )}
                            {renderItemPrice(item)}
                            {item.dispatchedAt && !item.isCancelled && (
                              <Text
                                type="secondary"
                                style={{
                                  display: 'block',
                                  fontSize: isMobile ? '10px' : '12px',
                                  marginTop: '2px'
                                }}
                              >
                                Despachado: {formatInTimeZone(item.dispatchedAt, 'HH:mm')}
                              </Text>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
                )}

                {orderItems.some(i => i.dailyMenuId) && (
                  <Collapse
                    accordion
                    ghost
                    size="small"
                    activeKey={dailyMenuActiveKey}
                    onChange={(k) => setDailyMenuActiveKey(Array.isArray(k) ? k[0] : k)}
                    style={{ marginTop: 4 }}
                  >
                    {orderItems.filter(i => i.dailyMenuId).map((item, index) => {
                      const panelKey = item.id || `dm-${index}`;
                      const isActive = dailyMenuActiveKey === panelKey;
                      const courses = groupSelectionsByCourse(item.dailyMenuSelections);
                      const selectableCount = item.dailyMenuSelectableCourseCount ?? 0;
                      const sentCount = (item.dailyMenuSelections || []).filter(s => s.isSelectable).length;
                      const canAddCourses =
                        !item.isCancelled &&
                        activeOrder?.status !== 'Closed' &&
                        activeOrder?.status !== 'Cancelled' &&
                        selectableCount > 0 &&
                        sentCount < selectableCount;
                      return (
                        <Panel
                          key={panelKey}
                          showArrow={false}
                          header={
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              width: '100%',
                              gap: 8,
                              opacity: item.isCancelled ? 0.5 : 1,
                            }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Space size="small" wrap>
                                  <Text
                                    strong={!item.isCancelled}
                                    style={{
                                      textDecoration: item.isCancelled ? 'line-through' : 'none',
                                      fontSize: isMobile ? '13px' : '14px'
                                    }}
                                  >
                                    {item.seatNumber && <SeatPill seatNumber={item.seatNumber} size="small" style={{ marginRight: 6 }} />}
                                    {item.quantity}x {item.dailyMenuName || item.productName}
                                  </Text>
                                  <CaretRightOutlined
                                    rotate={isActive ? 90 : 0}
                                    style={{ fontSize: 10, color: 'var(--text-secondary)', transition: 'transform 0.2s' }}
                                  />
                                  {item.isCancelled && (
                                    <Tag color="red" style={{ fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>
                                      CANCELADO
                                    </Tag>
                                  )}
                                </Space>
                                {item.notes && (
                                  <Text type="secondary" italic style={{ display: 'block', fontSize: isMobile ? '11px' : '13px' }}>
                                    Nota: {item.notes}
                                  </Text>
                                )}
                                <div>{renderItemPrice(item)}</div>
                                {item.dispatchedAt && !item.isCancelled && (
                                  <Text type="secondary" style={{ display: 'block', fontSize: isMobile ? '10px' : '12px' }}>
                                    Despachado: {formatInTimeZone(item.dispatchedAt, 'HH:mm')}
                                  </Text>
                                )}
                              </div>
                              {!item.isCancelled && itemRowMenu(item)}
                            </div>
                          }
                        >
                          {courses.length === 0 ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>Sin selecciones</Text>
                          ) : (
                            <div style={{ paddingLeft: 8 }}>
                              {courses.map(c => (
                                <div key={c.sortOrder} style={{ marginBottom: 2 }}>
                                  <Text strong style={{ fontSize: isMobile ? 11 : 13 }}>{c.courseName}:</Text>{' '}
                                  <Text style={{ fontSize: isMobile ? 11 : 13 }}>{c.options.join(', ')}</Text>
                                </div>
                              ))}
                            </div>
                          )}
                          {canAddCourses && (
                            <Button
                              type="link"
                              size="small"
                              icon={<PlusCircleOutlined />}
                              onClick={(e) => { e.stopPropagation(); setAddCoursesItem(item); }}
                              style={{ paddingLeft: 0, marginTop: 6 }}
                            >
                              Agregar tiempos faltantes
                            </Button>
                          )}
                        </Panel>
                      );
                    })}
                  </Collapse>
                )}
              </>
            )}
          </Card>
        </Col>

      </Row>

      {isPanel && (
        <div style={{
          flexShrink: 0,
          borderTop: '1px solid var(--border-color)',
          paddingTop: 12,
        }}>
          {totalAndActionsBar}
        </div>
      )}

      {/* Tablet y escritorio angosto: igual que en el teléfono, el total y las
          acciones se quedan fijos al fondo de la ventana — con la cuenta corta
          una barra pegajosa se despegaba y quedaba flotando a media pantalla.
          Los bordes van medidos para no meterse bajo el riel del sidebar. */}
      {isStacked && (
        <div
          ref={bottomBarRef}
          style={{
            position: 'fixed',
            bottom: 0,
            left: barInset?.left ?? 0,
            right: barInset?.right ?? 0,
            zIndex: 100,
            padding: '12px 16px',
            backgroundColor: 'var(--bg-primary)',
            borderTop: '1px solid var(--border-color)',
            boxShadow: '0 -4px 12px var(--shadow-color)',
          }}
        >
          {totalAndActionsBar}
        </div>
      )}
      </>
      )}

      {/* Modals */}
      {activeOrder && (
        <EditGuestsModal
          open={guestsVisible}
          onClose={() => setGuestsVisible(false)}
          orderId={activeOrder.id}
          tableId={table.id}
          tableNumber={table.number}
          adults={activeOrder.adults ?? 1}
          children={activeOrder.children ?? 0}
          capacity={totalCapacity}
        />
      )}

      <PreCheckModal
        visible={precuentaVisible}
        onCancel={() => setPrecuentaVisible(false)}
        table={table}
        order={activeOrder}
        onPrintAndSave={async () => {
          if (!activeOrder) return;
          await onPrecuenta?.(table, activeOrder);
          setPrecuentaVisible(false);
          onTableUpdate?.(); // Refresh table data
          refetchOrder(); // Refresh order data
        }}
      />

      <PaymentModal
        visible={paymentVisible}
        onCancel={() => setPaymentVisible(false)}
        table={table}
        order={activeOrder}
        discountAmount={discountAmount}
        discountPercentage={discountPercentage}
        onProcessPayment={async (payments, tip) => {
          if (!activeOrder) {
            message.error('No hay orden activa para procesar el pago');
            return;
          }
          
          try {
            // Call the parent's payment handler with the required data structure
            await onProcessPayment?.(payments, tip, table, activeOrder);
            setPaymentVisible(false);
            // Note: No need to refresh data here as TablesManager will handle
            // returning to main view and the payment handler will update status
          } catch (error: any) {
            console.error('Error processing payment:', error);

            // La orden ya está dividida pero esta pantalla traía el estado viejo
            // y ofreció "Liquidar Cuenta". El backend lo rechaza; en vez de dejar
            // a la cajera atorada, se abre el cobro por cuenta.
            if (isOrderAlreadySplitError(error)) {
              message.warning(ORDER_ALREADY_SPLIT_NOTICE);
              goToSplitPayment();
            }
            throw error;
          }
        }}
        onPrintReceipt={(payments, tip) => onPrintReceipt?.(payments, tip, table, activeOrder)}
        onOrderAlreadySplit={goToSplitPayment}
        terminalTarget={activeOrder ? { orderId: activeOrder.id, tableId: table.id } : undefined}
        onTerminalPaymentSettled={async () => {
          setPaymentVisible(false);
          await onTerminalPaymentSettled?.(table, activeOrder);
        }}
      />

      <SplitBillModal
        visible={splitVisible}
        onCancel={() => {
          setSplitVisible(false);
          refetchOrder(); // isSplit pudo cambiar (división creada o cancelada)
        }}
        table={table}
        order={activeOrder}
        onOrderClosed={async () => {
          setSplitVisible(false);
          // La mesa la pasa a Sucia el backend al cobrarse la última cuenta
          // (OrderSplitService → ITableService.MarkTableDirtyAfterPaymentAsync),
          // igual que en el cobro de la orden completa.
          message.success('Pago procesado exitosamente. Mesa marcada como sucia');
          onTableUpdate?.();
          onBack();
        }}
      />

      <ItemDiscountModal
        visible={!!itemDiscountTarget}
        order={activeOrder}
        item={itemDiscountTarget}
        onCancel={() => setItemDiscountTarget(null)}
        onApplied={() => {
          setItemDiscountTarget(null);
          refetchDiscounts();
          refetchOrder();
        }}
      />

      <DiscountModal
        visible={discountVisible}
        onItemDiscountApplied={() => {
          refetchDiscounts();
          refetchOrder();
        }}
        onCancel={() => setDiscountVisible(false)}
        table={table}
        order={activeOrder}
        onApplyDiscount={async (discountData) => {
          try {
            // Call the TablesManager handler to apply discount to backend
            await onApplyDiscountToOrder?.({ ...discountData, orderId: activeOrder?.id });
            
            // Update local state for payment calculations
            if (discountData.type === 'percentage') {
              setDiscountPercentage(discountData.value);
              setDiscountAmount(0);
            } else {
              setDiscountAmount(discountData.value);
              setDiscountPercentage(0);
            }
            
            setDiscountVisible(false);
            // Don't return to payment modal - stay in table menu to show applied discount
            await refetchOrder();
            await refetchDiscounts(); // Refresh discount data
          } catch (error) {
            console.error('Error applying discount:', error);
            // Don't close modal on error
            throw error; // Re-throw to let DiscountModal handle the error display
          }
        }}
        onDiscountRemoved={() => {
          // Reset legacy local discount state and refresh totals
          setDiscountAmount(0);
          setDiscountPercentage(0);
          refetchOrder();
          refetchDiscounts();
        }}
      />

      {/* Mobile: secondary actions drawer (primary actions live in the bottom bar) */}
      <Drawer
        title="Más acciones"
        placement="bottom"
        onClose={() => setActionsDrawerVisible(false)}
        open={actionsDrawerVisible}
        height="auto"
        bodyStyle={{ padding: '16px' }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {secondaryActions.map(action => renderActionButton(action, { closeDrawer: true }))}
          {moreActions.map(action => renderActionButton(action, { closeDrawer: true }))}

          <Divider style={{ margin: '12px 0' }} />

          {forceCloseButton(true)}
        </Space>
      </Drawer>

      {/* Mobile: total + primary actions pinned to the bottom */}
      {isMobile && !comandearVisible && (
        <div ref={bottomBarRef} style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          padding: '10px 12px',
          paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
          backgroundColor: 'var(--bg-primary)',
          borderTop: '1px solid var(--border-color)',
          boxShadow: '0 -4px 12px var(--shadow-color)',
        }}>
          {totalAndActionsBar}
        </div>
      )}

      {/* Order History */}
      {hasActiveOrder && (
        <OrderHistory
          orderId={activeOrder.id}
          visible={orderHistoryVisible}
          onClose={() => setOrderHistoryVisible(false)}
          orderNumber={String(activeOrder.orderNumber)}
          tableNumber={table.number.toString()}
        />
      )}

      {/* Table History */}
      <TableHistory
        tableId={table.id}
        tableName={`Mesa ${table.number} (${table.areaName})`}
        visible={tableHistoryVisible}
        onClose={() => setTableHistoryVisible(false)}
      />

      {activeOrder && (
        <ResendOrderModal
          visible={resendModalVisible}
          onCancel={() => setResendModalVisible(false)}
          onConfirm={handleResendConfirm}
          orderId={activeOrder.id}
        />
      )}

      {addCoursesItem && activeOrder && (
        <DailyMenuSelectionModal
          visible={!!addCoursesItem}
          onCancel={() => setAddCoursesItem(null)}
          onAddItem={async (item: ProductOrderItem) => {
            const newSelections = (item.dailyMenuSelections || [])
              .filter(s => !!s.optionId)
              .map(s => ({ courseId: s.courseId, optionId: s.optionId! }));
            const tableLabel = `${table.areaName} - ${table.number}`;
            const ok = await sendIncrementalDailyMenu({
              order: activeOrder,
              orderItem: addCoursesItem,
              newSelections,
              tableLabel,
            });
            if (ok) setAddCoursesItem(null);
          }}
          initialValues={{
            dailyMenuId: addCoursesItem.dailyMenuId!,
            quantity: 1,
            notes: addCoursesItem.notes,
            dailyMenuSelections: (addCoursesItem.dailyMenuSelections || []).map(s => ({
              courseId: s.dailyMenuCourseId,
              courseName: s.courseName,
              optionId: s.dailyMenuOptionId,
              optionName: s.optionName,
              priceModifier: s.priceModifier,
              sortOrder: s.courseSortOrder,
            })),
          }}
          sentCourseIds={(addCoursesItem.dailyMenuSelections || [])
            .filter(s => s.isSelectable)
            .map(s => s.dailyMenuCourseId)}
        />
      )}
    </div>
  );
};

export default TableManagement;